import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Map, MessageSquare, Target, Settings, Menu, ChevronLeft, ChevronDown, Store, Building2, BarChart3, Heart, Activity, LayoutDashboard } from 'lucide-react';
interface SidebarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
}
interface MenuItem {
  id: string;
  label: string;
  icon: React.ElementType;
}
interface MenuSection {
  id: string;
  label: string;
  icon: React.ElementType;
  items: MenuItem[];
}
const menuSections: MenuSection[] = [{
  id: 'konzern',
  label: 'Konzern-Reputation',
  icon: Building2,
  items: [{
    id: 'konzern-uebersicht',
    label: 'Übersicht',
    icon: LayoutDashboard
  }, {
    id: 'heatmap',
    label: 'Reputationskarte',
    icon: Map
  }, {
    id: 'general-stimmen',
    label: 'Stimmen & Bewertungen',
    icon: MessageSquare
  }, {
    id: 'konzern-stimmung',
    label: 'Stimmungsbarometer',
    icon: Activity
  }]
}, {
  id: 'markt',
  label: 'Markt-Reputation',
  icon: Store,
  items: [{
    id: 'markt-stimmung',
    label: 'Stimmungsbarometer',
    icon: Heart
  }, {
    id: 'bewertungs-hub',
    label: 'Stimmen & Bewertungen',
    icon: MessageSquare
  }]
}, {
  id: 'wettbewerb',
  label: 'Mitbewerber',
  icon: Target,
  items: [{
    id: 'bewertungsvergleich',
    label: 'Bewertungsvergleich',
    icon: BarChart3
  }, {
    id: 'themenvergleich',
    label: 'Themenvergleich',
    icon: MessageSquare
  }]
}];
export function Sidebar({
  activeTab,
  onTabChange
}: SidebarProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const toggleSection = (sectionId: string) => {
    if (isCollapsed) {
      setIsCollapsed(false);
      setExpandedSections([sectionId]);
    } else {
      setExpandedSections(prev => prev.includes(sectionId) ? prev.filter(id => id !== sectionId) : [...prev, sectionId]);
    }
  };
  const handleItemClick = (itemId: string, sectionId: string) => {
    onTabChange(itemId);
    if (!expandedSections.includes(sectionId)) {
      setExpandedSections(prev => [...prev, sectionId]);
    }
  };
  // Find which section contains the active tab
  const getActiveSectionId = () => {
    for (const section of menuSections) {
      if (section.items.some(item => item.id === activeTab)) {
        return section.id;
      }
    }
    return null;
  };
  const activeSectionId = getActiveSectionId();
  return <motion.aside initial={false} animate={{
    width: isCollapsed ? 72 : 280
  }} className="bg-white h-screen flex flex-col fixed left-0 top-0 z-50 shadow-[4px_0_24px_rgba(0,0,0,0.02)] border-r border-gray-100 text-gray-900 overflow-hidden" transition={{
    duration: 0.2,
    ease: 'easeInOut'
  }} data-id="element-14">
      {/* Header / Logo */}
      <div className="h-[72px] flex items-center px-5 border-b border-gray-100 relative shrink-0" data-id="element-15">
        <div className="flex items-center gap-3 overflow-hidden whitespace-nowrap" data-id="element-16">
          <img src="https://www.obi.de/assets/icons/logos/obi-logo-orange.svg" alt="OBI Logo" className="h-6 w-auto shrink-0" data-id="element-17" />
          <motion.div animate={{
          opacity: isCollapsed ? 0 : 1
        }} className="flex items-center gap-2" data-id="element-18">
            <div className="flex flex-col leading-none mt-1" data-id="element-19">
              <span className="text-[12px] font-semibold text-gray-900 tracking-wide" data-id="element-20">
                Reputation
              </span>
              <span className="text-[11px] font-medium text-obi-orange" data-id="element-21">
                Intelligence
              </span>
            </div>
          </motion.div>
        </div>

        <button onClick={() => setIsCollapsed(!isCollapsed)} className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1.5 hover:bg-gray-100 rounded-md text-gray-400 hover:text-gray-900 transition-colors" data-id="element-22">
          {isCollapsed ? <Menu size={18} data-id="element-23" /> : <ChevronLeft size={18} data-id="element-24" />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200" data-id="element-25">
        {menuSections.map(section => {
        const isSectionExpanded = expandedSections.includes(section.id);
        const isSectionActive = activeSectionId === section.id;
        const SectionIcon = section.icon;
        return <div key={section.id} className="mb-2" data-id="element-26">
              {/* Section Header */}
              <button onClick={() => toggleSection(section.id)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group ${isSectionActive ? 'bg-obi-orange/10 text-obi-orange' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-27">
                <SectionIcon size={20} className={`shrink-0 ${isSectionActive ? 'text-obi-orange' : 'text-gray-400 group-hover:text-gray-600'}`} data-id="element-28" />

                <motion.span animate={{
              opacity: isCollapsed ? 0 : 1,
              width: isCollapsed ? 0 : 'auto'
            }} className="flex-1 text-left text-sm font-semibold whitespace-nowrap overflow-hidden" data-id="element-29">
                  {section.label}
                </motion.span>

                {!isCollapsed && <motion.div animate={{
              rotate: isSectionExpanded ? 180 : 0
            }} transition={{
              duration: 0.2
            }} data-id="element-30">
                    <ChevronDown size={16} className={isSectionActive ? 'text-obi-orange/70' : 'text-gray-400'} data-id="element-31" />
                  </motion.div>}

                {/* Tooltip for collapsed state */}
                {isCollapsed && <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50" data-id="element-32">
                    {section.label}
                  </div>}
              </button>

              {/* Section Items */}
              <AnimatePresence initial={false} data-id="element-33">
                {isSectionExpanded && !isCollapsed && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.2,
              ease: 'easeInOut'
            }} className="overflow-hidden" data-id="element-34">
                    <div className="pl-4 mt-1 space-y-0.5" data-id="element-35">
                      {section.items.map(item => {
                  const isActive = activeTab === item.id;
                  const ItemIcon = item.icon;
                  return <button key={item.id} onClick={() => handleItemClick(item.id, section.id)} className={`w-full flex items-center gap-3 px-3 py-2 rounded-md transition-all duration-200 relative ${isActive ? 'bg-obi-orange/5 text-obi-orange font-medium' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-36">
                            {isActive && <div className="absolute left-0 inset-y-1 w-1 bg-obi-orange rounded-r-full" data-id="element-37" />}

                            <ItemIcon size={16} className={`shrink-0 ${isActive ? 'text-obi-orange' : 'text-gray-400'}`} data-id="element-38" />

                            <span className="text-sm whitespace-nowrap overflow-hidden text-ellipsis" data-id="element-39">
                              {item.label}
                            </span>
                          </button>;
                })}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>;
      })}
      </nav>

      {/* Footer / Settings */}
      <div className="p-3 border-t border-gray-100 shrink-0" data-id="element-40">
        <button className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 text-gray-600 hover:text-gray-900 hover:bg-gray-50" data-id="element-41">
          <Settings size={20} className="shrink-0 text-gray-400" data-id="element-42" />
          <motion.span animate={{
          opacity: isCollapsed ? 0 : 1,
          width: isCollapsed ? 0 : 'auto'
        }} className="whitespace-nowrap overflow-hidden text-sm font-medium" data-id="element-43">
            Einstellungen
          </motion.span>
        </button>

        <motion.div animate={{
        opacity: isCollapsed ? 0 : 1
      }} className="mt-3 px-3 pb-1" data-id="element-44">
          <div className="flex items-center gap-3" data-id="element-45">
            <div className="w-9 h-9 rounded-full bg-obi-orange text-white flex items-center justify-center font-bold text-sm shrink-0 shadow-sm" data-id="element-46">
              MS
            </div>
            <div className="overflow-hidden" data-id="element-47">
              <div className="text-sm font-semibold text-gray-900 truncate" data-id="element-48">
                Max Schmidt
              </div>
              <div className="text-xs text-gray-500 truncate" data-id="element-49">
                Leiter Analytics
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.aside>;
}